import { useState, useEffect } from 'react';
import { X, Check, Calendar, DollarSign, CreditCard, Banknote, FileText, Loader, AlertCircle, ChevronRight } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { captureLocation } from './locationTracker';

// Send auto-receipt email if enabled
async function sendAutoReceipt({ company, customer, amount, companyId, paymentMethod }) {
  if (!company?.auto_email_receipts) return;
  if (!customer?.email) return;
  try {
    await fetch('/api/send-receipt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        companyId,
        customerEmail: customer.email,
        customerName: customer.first_name || customer.name || '',
        amount,
        companyName: company.name || '',
        paymentMethod
      })
    });
    return customer.email;
  } catch (err) {
    console.warn('[AutoReceipt] Failed to send:', err.message);
    return null;
  }
}

export default function QuoteVisitCompletionModal({ job, companyId, onClose, onComplete, onNavigate }) {
  const { showAlert, showError, showSuccess, showConfirm } = useModal();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('outcome'); // 'outcome', 'schedule', 'payment', 'success'
  const [customer, setCustomer] = useState(null);
  const [company, setCompany] = useState(null);

  // Quote fee
  const [quoteFeeAmount, setQuoteFeeAmount] = useState(0);

  // Outcome selection
  const [outcome, setOutcome] = useState(null); // 'agreed_now', 'agreed_schedule', 'declined', 'reschedule'

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'cash', 'check'
  const [checkNumber, setCheckNumber] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  // New job creation
  const [createdJobId, setCreatedJobId] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [job.id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load company settings
      const { data: companyData } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      setCompany(companyData);

      // Set quote fee from job or company default
      const feeAmount = job.quote_fee_amount ?? companyData?.default_quote_fee_amount ?? 75;
      setQuoteFeeAmount(feeAmount);

      // Load customer
      const { data: customerData } = await supabaseData
        .from('customers')
        .select('*')
        .eq('id', job.customer_id)
        .single();
      setCustomer(customerData);

    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load job data');
    } finally {
      setLoading(false);
    }
  };

  const handleOutcomeSelect = async (selectedOutcome) => {
    setOutcome(selectedOutcome);

    if (selectedOutcome === 'agreed_now' || selectedOutcome === 'agreed_schedule') {
      // Customer agreed - waive fee and create new job
      await handleCustomerAgreed(selectedOutcome);
    } else if (selectedOutcome === 'declined') {
      // Customer declined - go to payment step
      if (quoteFeeAmount > 0) {
        setStep('payment');
      } else {
        // No fee, just mark as closed_lost
        await handleDeclined();
      }
    } else if (selectedOutcome === 'reschedule') {
      // Just close the modal, they'll reschedule from the calendar
      onClose();
    }
  };

  const handleCustomerAgreed = async (selectedOutcome) => {
    setSaving(true);
    try {
      // 1. Create new job linked to this quote visit
      const { data: newJob, error: jobError } = await supabaseData
        .from('jobs')
        .insert({
          company_id: companyId,
          customer_id: job.customer_id,
          quote_id: job.quote_id,
          notes: job.notes || '',
          job_type: 'repair',
          status: selectedOutcome === 'agreed_now' ? 'in-progress' : 'scheduled',
          scheduled_date: selectedOutcome === 'agreed_now' ? new Date().toISOString().split('T')[0] : null,
          scheduled_time: selectedOutcome === 'agreed_now' ? new Date().toTimeString().slice(0, 5) : null,
          quote_fee_credit_from_job_id: job.id,
          quote_fee_status: 'credited'
        })
        .select()
        .single();

      if (jobError) throw jobError;
      setCreatedJobId(newJob.id);

      // 2. Mark original quote visit as completed with fee waived
      const { error: updateError } = await supabaseData
        .from('jobs')
        .update({
          status: 'completed',
          quote_fee_status: 'waived',
          quote_fee_waived_reason: 'customer_agreed',
          completed_at: new Date().toISOString()
        })
        .eq('id', job.id);

      if (updateError) throw updateError;

      // Capture tech location on job complete
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser?.id) captureLocation(authUser.id, companyId, 'job_complete');

      if (selectedOutcome === 'agreed_now') {
        // Navigate to do the work now
        setStep('success');
        showSuccess('Quote visit completed! Starting new job...');
        setTimeout(() => {
          onComplete && onComplete(newJob);
        }, 1500);
      } else {
        // Go to scheduling - navigate to calendar
        showSuccess('Quote visit completed! Opening scheduler...');
        setTimeout(() => {
          onClose();
          onNavigate && onNavigate('schedule', { jobId: newJob.id });
        }, 1500);
      }

    } catch (error) {
      console.error('Error creating job:', error);
      showError('Failed to create job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeclined = async () => {
    setSaving(true);
    try {
      // Mark as closed_lost with no fee (fee was 0)
      const { error } = await supabaseData
        .from('jobs')
        .update({
          status: 'completed',
          quote_fee_status: 'waived',
          quote_fee_waived_reason: 'no_fee_configured'
        })
        .eq('id', job.id);

      if (error) throw error;

      showSuccess('Quote visit marked as closed');
      onComplete && onComplete();
      onClose();
    } catch (error) {
      console.error('Error updating job:', error);
      showError('Failed to update job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentCollection = async () => {
    setProcessingPayment(true);
    try {
      // 1. Create payment record
      const { data: payment, error: paymentError } = await supabaseData
        .from('payments')
        .insert({
          company_id: companyId,
          customer_id: job.customer_id,
          amount: quoteFeeAmount,
          payment_method: paymentMethod,
          reference_number: paymentMethod === 'check' ? checkNumber : null,
          notes: `Quote visit fee for job ${job.job_number || job.id}`,
          payment_date: new Date().toISOString().split('T')[0]
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // 2. Update job with payment info and mark as closed_lost
      const { error: updateError } = await supabaseData
        .from('jobs')
        .update({
          status: 'completed',
          quote_fee_status: 'charged',
          quote_fee_charged_at: new Date().toISOString(),
          quote_fee_payment_id: payment.id
        })
        .eq('id', job.id);

      if (updateError) throw updateError;

      showSuccess(`Payment of $${quoteFeeAmount.toFixed(2)} collected!`);
      // Auto-email receipt if enabled
      const emailedTo = await sendAutoReceipt({
        company, customer, amount: quoteFeeAmount, companyId, paymentMethod
      });
      if (emailedTo) {
        showSuccess(`Receipt emailed to ${emailedTo}`);
      }
      setStep('success');
      setTimeout(() => {
        onComplete && onComplete();
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error processing payment:', error);
      showError('Failed to process payment: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <Loader size={32} color={colors.accent} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: colors.text }}>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
              Complete Quote Visit
            </h2>
            <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
              {customer?.first_name} {customer?.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>

          {/* Step: Outcome Selection */}
          {step === 'outcome' && (
            <div>
              <p style={{ color: colors.textMuted, marginBottom: '1.5rem', textAlign: 'center' }}>
                What was the outcome of this quote visit?
              </p>

              {/* Quote Fee Display */}
              {quoteFeeAmount > 0 && (
                <div style={{
                  background: `${colors.accent}20`,
                  border: `1px solid ${colors.accent}50`,
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Quote Visit Fee
                  </div>
                  <div style={{ color: colors.accent, fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${quoteFeeAmount.toFixed(2)}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    Waived if customer agrees to work
                  </div>
                </div>
              )}

              {/* Outcome Options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  onClick={() => handleOutcomeSelect('agreed_now')}
                  disabled={saving}
                  style={{
                    padding: '1rem 1.25rem',
                    background: colors.bgInput,
                    border: `2px solid ${colors.success}`,
                    borderRadius: '12px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: `${colors.success}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Check size={20} color={colors.success} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold' }}>
                      Customer Agreed - Do Work Now
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      Fee waived, start job immediately
                    </div>
                  </div>
                  <ChevronRight size={20} color={colors.textMuted} />
                </button>

                <button
                  onClick={() => handleOutcomeSelect('agreed_schedule')}
                  disabled={saving}
                  style={{
                    padding: '1rem 1.25rem',
                    background: colors.bgInput,
                    border: `2px solid ${colors.accent}`,
                    borderRadius: '12px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: `${colors.accent}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Calendar size={20} color={colors.accent} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold' }}>
                      Customer Agreed - Schedule Job
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      Fee waived, schedule for later
                    </div>
                  </div>
                  <ChevronRight size={20} color={colors.textMuted} />
                </button>

                <button
                  onClick={() => handleOutcomeSelect('declined')}
                  disabled={saving}
                  style={{
                    padding: '1rem 1.25rem',
                    background: colors.bgInput,
                    border: `2px solid ${colors.warning}`,
                    borderRadius: '12px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: `${colors.warning}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <DollarSign size={20} color={colors.warning} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold' }}>
                      Customer Didn't Agree
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      {quoteFeeAmount > 0 ? `Collect $${quoteFeeAmount.toFixed(2)} fee` : 'Mark as closed/lost'}
                    </div>
                  </div>
                  <ChevronRight size={20} color={colors.textMuted} />
                </button>

                <button
                  onClick={() => handleOutcomeSelect('reschedule')}
                  disabled={saving}
                  style={{
                    padding: '1rem 1.25rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: colors.bgCard,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Calendar size={20} color={colors.textMuted} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold' }}>
                      Reschedule Visit
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      No fee action, reschedule from calendar
                    </div>
                  </div>
                  <ChevronRight size={20} color={colors.textMuted} />
                </button>
              </div>
            </div>
          )}

          {/* Step: Payment Collection */}
          {step === 'payment' && (
            <div>
              <div style={{
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: `${colors.warning}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <DollarSign size={32} color={colors.warning} />
                </div>
                <h3 style={{ color: colors.text, margin: '0 0 0.5rem' }}>
                  Collect Quote Visit Fee
                </h3>
                <div style={{ color: colors.accent, fontSize: '2rem', fontWeight: 'bold' }}>
                  ${quoteFeeAmount.toFixed(2)}
                </div>
              </div>

              {/* Payment Method Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  color: colors.textMuted,
                  fontSize: '0.85rem',
                  marginBottom: '0.5rem'
                }}>
                  Payment Method
                </label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: paymentMethod === 'card' ? `${colors.accent}20` : colors.bgInput,
                      border: paymentMethod === 'card' ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <CreditCard size={24} color={paymentMethod === 'card' ? colors.accent : colors.textMuted} />
                    <span style={{ color: colors.text, fontSize: '0.9rem' }}>Card</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: paymentMethod === 'cash' ? `${colors.accent}20` : colors.bgInput,
                      border: paymentMethod === 'cash' ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Banknote size={24} color={paymentMethod === 'cash' ? colors.accent : colors.textMuted} />
                    <span style={{ color: colors.text, fontSize: '0.9rem' }}>Cash</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('check')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: paymentMethod === 'check' ? `${colors.accent}20` : colors.bgInput,
                      border: paymentMethod === 'check' ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FileText size={24} color={paymentMethod === 'check' ? colors.accent : colors.textMuted} />
                    <span style={{ color: colors.text, fontSize: '0.9rem' }}>Check</span>
                  </button>
                </div>
              </div>

              {/* Check Number Input */}
              {paymentMethod === 'check' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    color: colors.textMuted,
                    fontSize: '0.85rem',
                    marginBottom: '0.5rem'
                  }}>
                    Check Number
                  </label>
                  <input
                    type="text"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="Enter check number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              {/* Card Payment Note */}
              {paymentMethod === 'card' && (
                <div style={{
                  background: `${colors.accent}10`,
                  border: `1px solid ${colors.accent}30`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ color: colors.accent, fontSize: '0.9rem' }}>
                    <AlertCircle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                    Card will be charged immediately upon confirmation
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setStep('outcome')}
                  disabled={processingPayment}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontWeight: 'bold',
                    cursor: processingPayment ? 'not-allowed' : 'pointer',
                    opacity: processingPayment ? 0.7 : 1
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handlePaymentCollection}
                  disabled={processingPayment || (paymentMethod === 'check' && !checkNumber)}
                  style={{
                    flex: 2,
                    padding: '1rem',
                    background: colors.success,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: (processingPayment || (paymentMethod === 'check' && !checkNumber)) ? 'not-allowed' : 'pointer',
                    opacity: (processingPayment || (paymentMethod === 'check' && !checkNumber)) ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {processingPayment ? (
                    <>
                      <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Confirm Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `${colors.success}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Check size={40} color={colors.success} />
              </div>
              <h3 style={{ color: colors.text, margin: '0 0 0.5rem' }}>
                {outcome === 'declined' ? 'Payment Collected!' : 'Quote Visit Complete!'}
              </h3>
              <p style={{ color: colors.textMuted, margin: 0 }}>
                {outcome === 'declined'
                  ? `$${quoteFeeAmount.toFixed(2)} has been recorded`
                  : 'New job has been created'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
